import { applyVideoTextOverridesToCommon } from '@/data/videos';
import { applyPlanNamePlaceholders, planNamesVersion } from '@/config/planNames';
import { applyCompanyYearPlaceholders, companyYearVars } from '@/utils/copyright';
import { isRouteAvailable } from '@/utils/routing';

export const localeNamespaces = [
  'common',
  'legal',
  'articles',
  'tradeLandings',
  'customerPortal',
  'bozpLanding',
  'forAccountants',
  'digitalBookkeeping',
  'longForm',
  'aiPage',
] as const;
export type LocaleCode = 'sk' | 'cs' | 'da' | 'en' | 'de';
export type LocaleNamespace = (typeof localeNamespaces)[number];

// One file per trade and language (locales/<lang>/trades/<trade>.json), so each
// trade page can be written on its own branch without touching the others.
const tradeFiles = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/trades/*.json');

async function loadTrades(lang: LocaleCode): Promise<{ default: Record<string, unknown> }> {
  const prefix = `./locales/${lang}/trades/`;
  const entries = await Promise.all(
    Object.entries(tradeFiles)
      .filter(([path]) => path.startsWith(prefix))
      .map(async ([path, load]) => [path.slice(prefix.length, -'.json'.length), (await load()).default] as const),
  );
  return { default: Object.fromEntries(entries) };
}

// Long SEO guides (locales/<lang>/longform/<name>.json) load only on their own
// routes, see namespaces.ts, so other pages do not download them.
const longFormFiles = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/longform/*.json');

async function loadLongForm(lang: LocaleCode): Promise<{ default: Record<string, unknown> }> {
  const prefix = `./locales/${lang}/longform/`;
  const entries = await Promise.all(
    Object.entries(longFormFiles)
      .filter(([path]) => path.startsWith(prefix))
      .map(async ([path, load]) => [path.slice(prefix.length, -'.json'.length), (await load()).default] as const),
  );
  return { default: Object.fromEntries(entries) };
}

// Self-contained page sections keep their copy in locales/<lang>/sections/<name>.json.
// It is merged into `common` under `sections.<name>`, so a section can ship on its
// own branch without editing the large shared common.json files.
const sectionFiles = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/sections/*.json');

async function loadSections(lang: LocaleCode): Promise<Record<string, unknown>> {
  const prefix = `./locales/${lang}/sections/`;
  const entries = await Promise.all(
    Object.entries(sectionFiles)
      .filter(([path]) => path.startsWith(prefix))
      .map(async ([path, load]) => [path.slice(prefix.length, -'.json'.length), structuredClone((await load()).default)] as const),
  );
  return Object.fromEntries(entries);
}

const loaders: Record<LocaleCode, Record<LocaleNamespace, () => Promise<{ default: Record<string, unknown> }>>> = {
  sk: {
    common: () => import('./locales/sk/common.json'),
    legal: () => import('./locales/sk/legal.json'),
    articles: () => import('./locales/sk/articles.json'),
    tradeLandings: () => loadTrades('sk'),
    customerPortal: () => import('./locales/sk/customerPortal.json'),
    bozpLanding: () => import('./locales/sk/bozpLanding.json'),
    forAccountants: () => import('./locales/sk/forAccountants.json'),
    digitalBookkeeping: () => import('./locales/sk/digitalBookkeeping.json'),
    longForm: () => loadLongForm('sk'),
    aiPage: () => import('./locales/sk/aiPage.json'),
  },
  cs: {
    common: () => import('./locales/cs/common.json'),
    legal: () => import('./locales/cs/legal.json'),
    articles: () => import('./locales/cs/articles.json'),
    tradeLandings: () => loadTrades('cs'),
    customerPortal: () => import('./locales/cs/customerPortal.json'),
    bozpLanding: () => import('./locales/cs/bozpLanding.json'),
    forAccountants: () => import('./locales/cs/forAccountants.json'),
    digitalBookkeeping: () => import('./locales/cs/digitalBookkeeping.json'),
    longForm: () => loadLongForm('cs'),
    aiPage: () => import('./locales/cs/aiPage.json'),
  },
  da: {
    common: () => import('./locales/da/common.json'),
    legal: () => import('./locales/da/legal.json'),
    articles: () => import('./locales/da/articles.json'),
    tradeLandings: () => loadTrades('da'),
    customerPortal: () => import('./locales/da/customerPortal.json'),
    bozpLanding: () => import('./locales/da/bozpLanding.json'),
    forAccountants: () => import('./locales/da/forAccountants.json'),
    digitalBookkeeping: () => import('./locales/da/digitalBookkeeping.json'),
    longForm: () => loadLongForm('da'),
    aiPage: () => import('./locales/da/aiPage.json'),
  },
  en: {
    common: () => import('./locales/en/common.json'),
    legal: () => import('./locales/en/legal.json'),
    articles: () => import('./locales/en/articles.json'),
    tradeLandings: () => loadTrades('en'),
    customerPortal: () => import('./locales/en/customerPortal.json'),
    bozpLanding: () => import('./locales/en/bozpLanding.json'),
    forAccountants: () => import('./locales/en/forAccountants.json'),
    digitalBookkeeping: () => import('./locales/en/digitalBookkeeping.json'),
    longForm: () => loadLongForm('en'),
    aiPage: () => import('./locales/en/aiPage.json'),
  },
  de: {
    common: () => import('./locales/de/common.json'),
    legal: () => import('./locales/de/legal.json'),
    articles: () => import('./locales/de/articles.json'),
    tradeLandings: () => loadTrades('de'),
    customerPortal: () => import('./locales/de/customerPortal.json'),
    bozpLanding: () => import('./locales/de/bozpLanding.json'),
    forAccountants: () => import('./locales/de/forAccountants.json'),
    digitalBookkeeping: () => import('./locales/de/digitalBookkeeping.json'),
    longForm: () => loadLongForm('de'),
    aiPage: () => import('./locales/de/aiPage.json'),
  },
};

const economicComparison = {
  sk: () => import('./locales/sk/economicComparison.json'),
  cs: () => import('./locales/cs/economicComparison.json'),
  da: () => import('./locales/da/economicComparison.json'),
  en: () => import('./locales/en/economicComparison.json'),
  // No German comparison pages yet; the route is empty in de.
  de: () => import('./locales/en/economicComparison.json'),
};

const cache = new Map<string, Record<string, unknown>>();
const companyYears = companyYearVars();
// Bundles are cached with plan names filled in; new live names start over.
let cachedPlanNamesVersion = planNamesVersion();

if (import.meta.hot) {
  import.meta.hot.accept(() => {
    cache.clear();
  });
}

/** Snapshot of `common.videos` before dashboard overrides are applied. */
export const videoBaseTexts: Record<string, unknown> = {};

export async function loadLocaleNamespace(
  language: string,
  namespace: string,
): Promise<Record<string, unknown>> {
  const lng = language as LocaleCode;
  const ns = namespace as LocaleNamespace;
  const key = `${lng}:${ns}`;
  if (cachedPlanNamesVersion !== planNamesVersion()) {
    cache.clear();
    cachedPlanNamesVersion = planNamesVersion();
  }
  const cached = cache.get(key);
  if (cached) return cached;

  const loader = loaders[lng]?.[ns];
  if (!loader) {
    throw new Error(`Unknown i18n bundle ${lng}/${ns}`);
  }

  const data = structuredClone((await loader()).default);

  if (ns === 'common') {
    const videos = (data as { videos?: unknown }).videos;
    if (videos && videoBaseTexts[lng] === undefined) {
      videoBaseTexts[lng] = structuredClone(videos);
    }
    applyVideoTextOverridesToCommon(data, lng);
    const sections = data as { sections?: Record<string, unknown> };
    sections.sections = { ...sections.sections, ...(await loadSections(lng)) };
    const pages = (data as { pages?: { comparisons?: Record<string, unknown> } }).pages;
    // Only markets that publish the e-conomic page get its copy; other sites
    // must not ship another country's comparison to the browser.
    if (pages?.comparisons && isRouteAvailable('comparisonEconomic', lng)) {
      pages.comparisons.economic = structuredClone((await economicComparison[lng]()).default);
    }
  }

  applyCompanyYearPlaceholders(data, companyYears);
  applyPlanNamePlaceholders(data, lng);
  cache.set(key, data);
  return data;
}

export async function loadLocaleNamespaces(
  language: string,
  namespaces: readonly string[],
): Promise<Record<string, Record<string, unknown>>> {
  const loaded: Record<string, Record<string, unknown>> = {};
  await Promise.all(
    namespaces.map(async (namespace) => {
      loaded[namespace] = await loadLocaleNamespace(language, namespace);
    }),
  );
  return loaded;
}
